import type { ArcadeLevel, ColorLevel, FillLevel, LogicLevel, SortingLevel, TileLevel } from "@nano/content";
import type { Equipment } from "@nano/contracts";
import { defaultGameConfig, type GameConfig, type HouseId } from "@nano/game-config";
import { useRef, useSyncExternalStore } from "react";
import type { OutboxEntry, SaveData, Stars } from "../services/save/types.ts";

export type Overlay = "shop" | "wardrobe" | "leaderboard" | "settings" | "pick-change" | "restore" | "achievements" | "get-app";
export type SyncStatus = "idle" | "syncing" | "offline" | "error" | "disabled" | "session_lost";

/** The level being played, tagged with its house. */
export type PuzzleLevel =
  | { readonly houseId: "sorting"; readonly level: SortingLevel }
  | { readonly houseId: "colors"; readonly level: ColorLevel }
  | { readonly houseId: "tiles"; readonly level: TileLevel }
  | { readonly houseId: "logic"; readonly level: LogicLevel }
  | { readonly houseId: "fill"; readonly level: FillLevel }
  | { readonly houseId: "arcade"; readonly level: ArcadeLevel };
export type PuzzleHouseId = PuzzleLevel["houseId"];

export type PuzzleHudState =
  | {
      readonly houseId: "sorting";
      readonly levelId: number;
      readonly actions: number;
      readonly maxMoves: number | null;
      readonly targetMoves: number;
      readonly canUndo: boolean;
      readonly itemsLeft: number;
      readonly hints: number;
    }
  | {
      readonly houseId: "colors";
      readonly levelId: number;
      /** Pours in the current line (after undos). */
      readonly pours: number;
      readonly canUndo: boolean;
      readonly hints: number;
      readonly stuck: boolean;
    }
  | {
      readonly houseId: "tiles";
      readonly levelId: number;
      /** Tiles still on the floor. */
      readonly left: number;
      /** Most tiles that rested in the satchel at once along the current line. */
      readonly peak: number;
      readonly canUndo: boolean;
      readonly hints: number;
      readonly stuck: boolean;
    }
  | {
      readonly houseId: "logic";
      readonly levelId: number;
      /** Drops in the current line (after undos). */
      readonly drops: number;
      readonly canUndo: boolean;
      readonly hints: number;
    }
  | {
      readonly houseId: "fill";
      readonly levelId: number;
      /** Rolls laid in the current line (after undos). */
      readonly rolls: number;
      readonly canUndo: boolean;
      readonly hints: number;
      readonly stuck: boolean;
    }
  | {
      readonly houseId: "arcade";
      readonly levelId: number;
      /** Coins in the current line (after undos). */
      readonly coins: number;
      readonly canUndo: boolean;
      readonly hints: number;
      readonly stuck: boolean;
    };

export interface ResultView {
  readonly levelId: number;
  readonly stars: Stars;
  readonly previousBest: Stars | null;
  readonly predictedGold: number;
  readonly timeMs: number | null;
  readonly previousBestTimeMs: number | null;
  readonly outboxId: number | null;
  readonly status: "pending" | "offline" | "confirmed" | "rejected" | "save_failed";
  readonly confirmedGold?: number;
  readonly reason?: string;
  /** Set when this first clear finished a chapter (level 10, 20, …). */
  readonly chapterComplete: number | null;
  readonly bestCombo: number;
  readonly houseId: PuzzleHouseId;
  /** Color Lab: pours in the kept line, and the level's thresholds for the explanation. */
  readonly pours?: { readonly used: number; readonly optimal: number; readonly three: number; readonly two: number; readonly hinted: boolean };
  /** Lối Ngọc: how full the satchel got, against the gold line and the tidiest route. */
  readonly satchel?: { readonly peak: number; readonly goldLine: number; readonly tidiest: number; readonly hinted: boolean };
  /** Tháp Bi Lật: drops in the kept line, and the level's thresholds for the explanation. */
  readonly drops?: { readonly used: number; readonly optimal: number; readonly three: number; readonly two: number; readonly hinted: boolean };
  /** Xưởng Phép Thuật: rolls in the kept line, and the level's thresholds for the explanation. */
  readonly rolls?: { readonly used: number; readonly optimal: number; readonly three: number; readonly two: number; readonly hinted: boolean };
  /** Máy Gắp Lém Lỉnh: coins in the kept line, and the level's thresholds for the explanation. */
  readonly coins?: { readonly used: number; readonly optimal: number; readonly three: number; readonly two: number; readonly hinted: boolean };
}

/** A downloaded update waiting for a safe moment (never mid-level). */
export interface UpdateOffer {
  readonly kind: "web" | "bundle" | "apk";
  readonly version: string;
}

export interface UiState {
  readonly screen: "boot" | "pick" | "city" | "puzzle";
  readonly overlay: Overlay | null;
  readonly lobbyHouse: HouseId | null;
  readonly infoHouse: HouseId | null;
  readonly puzzle: (PuzzleLevel & { readonly attempt: number }) | null;
  readonly hud: PuzzleHudState | null;
  /** Hints bought with gold for this attempt (on top of the free ones). */
  readonly paidHints: number;
  readonly paused: boolean;
  readonly result: ResultView | null;
  readonly outOfMoves: boolean;
  readonly tutorial: { readonly id: string; readonly step: number } | null;
  readonly bootProgress: number;
  readonly cityReady: boolean;
  readonly online: boolean;
  readonly sync: SyncStatus;
  readonly config: GameConfig;
  readonly toast: { readonly id: number; readonly text: string } | null;
  readonly contextLost: boolean;
  readonly previewEquip: Equipment | null;
  readonly durableSave: boolean;
  readonly levelLoading: boolean;
  readonly levelError: boolean;
  /** Achievement unlock banner (one or several at once). */
  readonly achievementPopup: { readonly key: number; readonly ids: readonly string[] } | null;
  /** Item just bought, shown in the reveal card. */
  readonly purchaseReveal: string | null;
  readonly update: UpdateOffer | null;
  /** iOS Safari: suggest "Add to Home Screen" before playing. */
  readonly installGuide: boolean;
  /** Full asset download before play (iOS). */
  readonly preload: { readonly done: number; readonly total: number; readonly failed: boolean } | null;
}

export interface AppState {
  readonly save: SaveData | null;
  readonly outbox: readonly OutboxEntry[];
  readonly ui: UiState;
}

type Listener = () => void;

export function createStore<T>(initial: T) {
  let state = initial;
  const listeners = new Set<Listener>();
  return {
    get: () => state,
    set(update: (current: T) => T) {
      const next = update(state);
      if (next === state) return;
      state = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const initialUi: UiState = {
  screen: "boot",
  overlay: null,
  lobbyHouse: null,
  infoHouse: null,
  puzzle: null,
  hud: null,
  paidHints: 0,
  paused: false,
  result: null,
  outOfMoves: false,
  tutorial: null,
  bootProgress: 0,
  cityReady: false,
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  sync: "idle",
  config: defaultGameConfig,
  toast: null,
  contextLost: false,
  previewEquip: null,
  durableSave: true,
  levelLoading: false,
  levelError: false,
  achievementPopup: null,
  purchaseReveal: null,
  update: null,
  installGuide: false,
  preload: null,
};

export const appStore = createStore<AppState>({ save: null, outbox: [], ui: initialUi });

export function setUi(patch: Partial<UiState> | ((ui: UiState) => Partial<UiState>)): void {
  appStore.set((state) => ({
    ...state,
    ui: { ...state.ui, ...(typeof patch === "function" ? patch(state.ui) : patch) },
  }));
}

let toastId = 0;
export function toast(text: string): void {
  toastId += 1;
  const id = toastId;
  setUi({ toast: { id, text } });
  setTimeout(() => setUi((ui) => (ui.toast?.id === id ? { toast: null } : {})), 3200);
}

export function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || !a || !b) return false;
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  return keysA.every((key) => Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
}

/** Subscribes a component to a slice of the store. */
export function useApp<S>(selector: (state: AppState) => S, equal: (a: S, b: S) => boolean = Object.is): S {
  const cache = useRef<{ state: AppState; value: S } | null>(null);
  const getSnapshot = () => {
    const state = appStore.get();
    const previous = cache.current;
    if (previous && previous.state === state) return previous.value;
    const value = selector(state);
    if (previous && equal(previous.value, value)) {
      cache.current = { state, value: previous.value };
      return previous.value;
    }
    cache.current = { state, value };
    return value;
  };
  return useSyncExternalStore(appStore.subscribe, getSnapshot, getSnapshot);
}

// Singletons (store, services, listeners) must never be duplicated by a hot update.
if (import.meta.hot) import.meta.hot.accept(() => window.location.reload());
